// Layer 4 (UI) — the Risk route's small glass pieces (polish.P20, locked comp
// `risk.html` A): comparison segments, the id code, the band chip, the owner
// chip and the reference chip. Presentational only; the chip and pill recipes
// are the house ones (Process `ToneChip`, Advisory `ActorChip`, Documents
// `REF`), reused rather than redrawn.

import type { ReactNode } from 'react';
import { cn } from '@/shared/cn';
import { ActorChip } from '../advisory/PipelineStrip';
import { ToneChip } from '../process/ProcessUi';
import type { ProcessTone } from '../process/Scene.transform';
import type { RiskBand, RiskTextSegment } from './Scene.transform';

/** A comparison: the lead term in 600 ink-secondary, the rest in the line's ink. */
export function Segments({ segments }: { segments: readonly RiskTextSegment[] }) {
  return (
    <>
      {segments.map((segment, index) =>
        segment.strong ? (
          <span key={index} className={cn('font-semibold', segment.bad ? 'text-[var(--state-blocked)]' : 'text-ink-secondary', segment.nowrap && 'whitespace-nowrap')}>{segment.text}</span>
        ) : (
          <span key={index} className={segment.nowrap ? 'whitespace-nowrap' : undefined}>{segment.text}</span>
        ),
      )}
    </>
  );
}

/** A risk, interlock or check id set as a code (comp `.idc`, tracked tabular Inter). */
export function IdCode({ children, ink = true, className }: { children: ReactNode; ink?: boolean; className?: string }) {
  return (
    <span className={cn('whitespace-nowrap font-mono text-[11.5px] tabular-nums', ink ? 'text-ink-primary' : 'text-muted', className)}>
      {children}
    </span>
  );
}

export const BAND_TONE: Record<RiskBand, ProcessTone> = {
  critical: 'bad',
  high: 'warn',
  medium: 'neutral',
  low: 'neutral',
};

/** A risk's band as a chip: Critical and High take state colour, the rest are quiet. */
export function BandChip({ band, label }: { band: RiskBand; label: string }) {
  return <ToneChip tone={BAND_TONE[band]} data-band={band}>{label}</ToneChip>;
}

/** Whoever owns or performs is a Human actor chip (DESIGN.md § Actor chips). */
export function OwnerChip({ children }: { children: ReactNode }) {
  return <ActorChip actor="Human">{children}</ActorChip>;
}

/** A reference chip: a work order, 3W or case named inline (comp `.ref`). */
export const REF_CHIP =
  'inline-block whitespace-nowrap rounded-md border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-1.5 font-mono text-[11px] leading-[1.35] text-ink-secondary';

/** The same chip as a link into another route (Advisory case pages). */
export const REF_LINK = cn(REF_CHIP, 'transition-colors hover:bg-[var(--surface-1)] hover:text-ink-primary');

/** A failure-mode tag (comp `.fm`): outlined, sentence case. */
export function FailureModeTag({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex whitespace-nowrap rounded-full border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-2 text-[11px] leading-[1.45] text-ink-secondary">
      {children}
    </span>
  );
}
